// src/utils/phoneNumber.js
// Shared phone helpers and the pure pieces of the LoginScreen changes of the
// CritCast desktop app v1 (DESKTOP_APP_DESIGN_2026-10-20.md S2.4, S2.2 items
// 4 and 8, S2.6, S7.6 choice 3; unit W2).
//
// What lives here:
//  - toE164 (moved verbatim from PhoneVerificationScreen) and
//    phoneSendErrorText(e, { screen }) with the S2.4 error table, so
//    LoginScreen and PhoneVerificationScreen say the same thing;
//  - PHONE_REGION_HINT: one hint for both screens. The SMS region policy is
//    project-wide (a founder console step), so the hint, the two
//    invalid-number lines and the blocked-region error line change together
//    with it (phoneRegionCopy over PHONE_ALLOWED_REGIONS);
//  - phone sign-in must NOT create accounts: afterPhoneConfirm decides from
//    getAdditionalUserInfo(result) whether the fresh Auth user is deleted, and
//    deleteOrSignOut runs that delete (signOut when the delete throws);
//  - isPhoneOnlyRecord for the FinishOnboardingModal phone branch, and
//    isPhoneOnlyUser / blocksPhoneOnlyCompletion for SignUpWizard's
//    completion mode and ProfileSettings' "Complete My Profile";
//  - the client throttles (30 s send lock, 3 sends per mount, 5 wrong codes)
//    and their own line (PHONE_ERR.clientLocked: waiting never clears them);
//  - createRecaptchaSlot: LoginScreen's invisible reCAPTCHA, each verifier in
//    a fresh host element so a retry after a send error renders cleanly;
//  - loginGuard (the desktop guard: a signed-in member inside the app sees
//    "Sign out first" instead of the sign-in forms), nextTarget (the
//    `next` allowlist) and saveOAuthNext / takeOAuthNext (next across the
//    Discord/Twitch round trip), the desktopAuth=blocked line;
//  - signOutWithDesktopPush: the explicit sign-out buttons tell the desktop
//    app first (pushSession(null, { userAction: true })), then sign out.
//
// Copy rules: CritCast is the product; no em dashes; never "AI"; ASCII only.
// Never log a phone number or a raw Firebase message: callers log e.code only.
//
// Zero imports: unit-tested in plain node
// (web-landing/scripts/test-phone-signin.mjs). Firebase calls are passed in.

// ---------------------------------------------------------------------------
// E.164
// ---------------------------------------------------------------------------

// Firebase Phone Auth only accepts E.164 (+15551234567). Users type
// numbers every which way, so normalize before sending. 10 digits is
// assumed US; anything else must carry its own country code.
export const toE164 = (raw) => {
  const trimmed = (raw || '').trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (hasPlus) return digits.length >= 8 ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
};

// ---------------------------------------------------------------------------
// Strings (S2.4, S2.2 item 8, S2.6)
// ---------------------------------------------------------------------------

// The hint under the number field and the two invalid-number lines follow
// the project's SMS region policy (a founder console step, S2.4). Default
// policy = allowlist: US + CA + every region the read-only member probe
// reports (S8.4 step 6). PHONE_ALLOWED_REGIONS is that list as ISO 3166
// codes. Change it in the same sitting as the console policy, so the screens
// never tell an allowed member their number will not work and never invite a
// number the policy blocks (no "+44" example while only US and CA are open).
// Under the denylist alternative the hint is the old PhoneVerificationScreen
// line.
export const PHONE_REGION_POLICY = 'allowlist';
// Founder 2026-10-21: the console policy allows United States, Canada, the
// European Union group and the United Kingdom (probe: 2 members, both +1).
export const PHONE_ALLOWED_REGIONS = Object.freeze(['US', 'CA', 'GB', 'EU']);

export const PHONE_REGION_HINTS = Object.freeze({
  allowlist: 'US and Canada numbers work today. Elsewhere? Email support@critcash.com.',
  allowlistMore: 'Outside the US? Include your country code. Not every country works yet; if yours does not, email support@critcash.com.',
  denylist: 'Outside the US? Include your country code, e.g. +44 7911 123456.',
});

const INVALID_LINES = Object.freeze({
  northAmerica: 'Enter a valid US or Canada phone number, e.g. (555) 123-4567.',
  allowlistMore: 'Enter a valid phone number, e.g. (555) 123-4567. Include your country code if outside the US.',
  denylist: 'Enter a valid phone number, e.g. (555) 123-4567. Include a country code like +44 if outside the US.',
});
const FIREBASE_INVALID_LINES = Object.freeze({
  northAmerica: "That phone number isn't valid. Double-check it and try again.",
  withCountryCode: "That phone number isn't valid. Double-check it (include your country code if outside the US).",
});

// True when the allowlist is exactly US + CA (the Default before the probe
// adds a region).
function northAmericaOnly(regions) {
  const set = new Set((Array.isArray(regions) ? regions : []).map((r) => String(r).toUpperCase()));
  return set.size === 2 && set.has('US') && set.has('CA');
}

// { hint, invalid, invalidNumber } for a policy and its allowlist:
//   hint          the line under the number field (both screens);
//   invalid       toE164 refused the typed number (PHONE_INVALID_TEXT);
//   invalidNumber Firebase refused it (auth/invalid-phone-number).
export function phoneRegionCopy(policy = PHONE_REGION_POLICY, regions = PHONE_ALLOWED_REGIONS) {
  if (policy === 'allowlist' && northAmericaOnly(regions)) {
    return {
      hint: PHONE_REGION_HINTS.allowlist,
      invalid: INVALID_LINES.northAmerica,
      invalidNumber: FIREBASE_INVALID_LINES.northAmerica,
    };
  }
  if (policy === 'allowlist') {
    return {
      hint: PHONE_REGION_HINTS.allowlistMore,
      invalid: INVALID_LINES.allowlistMore,
      invalidNumber: FIREBASE_INVALID_LINES.withCountryCode,
    };
  }
  return {
    hint: PHONE_REGION_HINTS.denylist,
    invalid: INVALID_LINES.denylist,
    invalidNumber: FIREBASE_INVALID_LINES.withCountryCode,
  };
}

export const PHONE_REGION_COPY = Object.freeze(phoneRegionCopy());
export const PHONE_REGION_HINT = PHONE_REGION_COPY.hint;

export const PHONE_SIGN_IN_LABEL = 'Sign in with phone';
export const PHONE_NUMBER_PLACEHOLDER = '(555) 123-4567';
export const TEXT_ME_A_CODE_LABEL = 'Text me a code';
export const BACK_TO_EMAIL_LABEL = 'Back to email sign-in';
export const SMS_RATES_LINE = 'Standard text message rates may apply.';
export const PHONE_CODE_PLACEHOLDER = '6-digit code';
export const PHONE_CONFIRM_LABEL = 'Sign in';
export const USE_DIFFERENT_NUMBER_LABEL = 'Use a different number';
export const TEXT_ME_A_NEW_CODE_LABEL = 'Text me a new code';

export const PHONE_INVALID_TEXT = PHONE_REGION_COPY.invalid;
export const PHONE_CODE_REQUIRED_TEXT = 'Please enter the 6-digit code we sent you.';
// The pinned message (P3): shown when a phone sign-in found no account.
export const PHONE_NO_ACCOUNT_TEXT = 'No CritCast account uses this number yet. Sign in with your email or Google and add your phone under Verification.';
// PhoneVerificationScreen, auth/credential-already-in-use. A phone sign-in
// whose client delete failed leaves a record holding the number until the
// purgePhoneGhosts sweep removes it (10-minute age floor, every 10 minutes),
// so the line names the wait before it names support.
export const PHONE_ALREADY_LINKED_TEXT = 'That phone number is already linked to a different CritCast account. If you just tried phone sign-in with it, wait about 20 minutes and try again. Otherwise, contact support@critcash.com.';

// Error table (S2.4). Keys are the lines; phoneSendErrorText picks one.
export const PHONE_ERR = Object.freeze({
  invalidNumber: PHONE_REGION_COPY.invalidNumber,
  // Firebase's own limit (auth/too-many-requests): waiting does clear it.
  tooMany: 'Too many attempts. Please wait a bit and try again.',
  // The client throttle below (3 sends or 5 wrong codes per LoginScreen
  // mount): waiting never clears it, only a fresh page does.
  clientLocked: 'Too many attempts on this page. Reload the page to try again, or sign in with your email.',
  quota: "We can't send codes right now. Please try again later.",
  captcha: "We couldn't confirm you're a person. Reload the page and try again.",
  regionLogin: "Phone sign-in isn't available for numbers from your country yet. Sign in with your email.",
  regionVerification: "Phone verification isn't available for numbers from your country yet. Email support@critcash.com.",
  notAllowedLogin: "Phone sign-in isn't available right now. Sign in with your email or Google.",
  notAllowedVerification: "Phone verification isn't available right now. Try again later.",
  network: 'Check your connection and try again.',
  wrongCode: "That code didn't match. Check the 6 digits and try again.",
  codeExpired: 'That code expired. Tap Text me a new code.',
  unknownLogin: 'Sign-in failed. Please try again.',
  // PhoneVerificationScreen's unknown line: the member there is already
  // signed in, so "Sign-in failed" would name the wrong action.
  unknownVerification: 'Verification failed. Please try again.',
});

const CAPTCHA_CODES = [
  'auth/captcha-check-failed',
  'auth/invalid-app-credential',
  'auth/missing-app-credential',
  'auth/invalid-recaptcha-token',
  'auth/missing-recaptcha-token',
];

// e = a Firebase AuthError ({ code, message }); screen = 'login' (default) or
// 'verification' (PhoneVerificationScreen). Returns the member line, or null
// for auth/user-disabled on the login screen (the caller routes to
// RestoreAccount). Never returns e.message.
export function phoneSendErrorText(e, { screen = 'login' } = {}) {
  const onVerification = screen === 'verification';
  const code = e && typeof e.code === 'string' ? e.code : '';
  const message = e && typeof e.message === 'string' ? e.message : '';
  switch (code) {
    case 'auth/invalid-phone-number':
    case 'auth/missing-phone-number':
      return PHONE_ERR.invalidNumber;
    case 'auth/too-many-requests':
      return PHONE_ERR.tooMany;
    case 'auth/quota-exceeded':
      return PHONE_ERR.quota;
    case 'auth/operation-not-allowed':
      if (/region/i.test(message)) return onVerification ? PHONE_ERR.regionVerification : PHONE_ERR.regionLogin;
      return onVerification ? PHONE_ERR.notAllowedVerification : PHONE_ERR.notAllowedLogin;
    case 'auth/network-request-failed':
      return PHONE_ERR.network;
    case 'auth/invalid-verification-code':
      return PHONE_ERR.wrongCode;
    case 'auth/code-expired':
      return PHONE_ERR.codeExpired;
    case 'auth/user-disabled':
      return onVerification ? PHONE_ERR.unknownVerification : null;
    default:
      if (CAPTCHA_CODES.includes(code)) return PHONE_ERR.captcha;
      return onVerification ? PHONE_ERR.unknownVerification : PHONE_ERR.unknownLogin;
  }
}

// "We texted a code to +1 xxx xxx 1234." Last 4 digits only (streamers
// capture their screens live). A non +1 number has no fixed grouping, so it
// reads "your number ending in 3456".
export function codeSentLine(e164) {
  const digits = String(e164 || '').replace(/\D/g, '');
  const last4 = digits.slice(-4);
  if (/^\+1\d{10}$/.test(String(e164 || ''))) return `We texted a code to +1 xxx xxx ${last4}.`;
  return `We texted a code to your number ending in ${last4}.`;
}

// ---------------------------------------------------------------------------
// After confirm: phone sign-in never creates an account (S2.4 steps 4-5)
// ---------------------------------------------------------------------------

// info = getAdditionalUserInfo(result). Only a record Firebase just created is
// deleted; an existing record (isNewUser false or unknown) goes Home and the
// App.js guards apply as for any sign-in (S8.1 phone row: no client delete of
// a non-new record).
export function afterPhoneConfirm(info) {
  return info && info.isNewUser === true ? 'delete' : 'home';
}

// Runs the delete. deleteUser "deletes and signs out the user"; if it throws
// (network), signOut instead and the purgePhoneGhosts sweep removes the
// stranded record. Either way the screen shows the same pinned message.
export async function deleteOrSignOut({ user, auth, deleteUser, signOut }) {
  try {
    await deleteUser(user);
    return { deleted: true, message: PHONE_NO_ACCOUNT_TEXT };
  } catch (_) {
    try {
      await signOut(auth);
    } catch (__) {
      // Nothing more to do on the client; the sweep is the backstop.
    }
    return { deleted: false, message: PHONE_NO_ACCOUNT_TEXT };
  }
}

// FinishOnboardingModal phone branch (S2.4 item 3): an Auth record whose only
// providers are phone, with no email. providerData = Firebase's array of
// { providerId } (plain strings are accepted too). Empty providerData is NOT
// phone-only: Discord and Twitch members are created with none.
export function isPhoneOnlyRecord(providerData, email) {
  const list = Array.isArray(providerData) ? providerData : [];
  if (list.length === 0) return false;
  if (typeof email === 'string' ? email.trim() !== '' : !!email) return false;
  return list.every((p) => (typeof p === 'string' ? p : p && p.providerId) === 'phone');
}

// The same test on a Firebase User (or null). SignUpWizard's completion mode
// and ProfileSettings' "Complete My Profile" ask it before they call or open
// completeOAuthSignup: a phone-only record never becomes an account from an
// unmodified client (P3). The server refusal (F4) stays the real gate.
export function isPhoneOnlyUser(user) {
  return !!user && isPhoneOnlyRecord(user.providerData, user.email);
}

// SignUpWizard: completion mode (oauthCompletion, or any oauthProvider,
// which ?email= alone sets) with a phone-only record signed in shows the
// pinned message and Log out instead of the wizard.
export function blocksPhoneOnlyCompletion({ completionMode, user } = {}) {
  return !!completionMode && isPhoneOnlyUser(user);
}

// ---------------------------------------------------------------------------
// Client throttles (S2.4, S2.9 item 7). Under Firebase's own limits.
// ---------------------------------------------------------------------------

export const PHONE_SEND_LOCK_MS = 30000;
export const PHONE_MAX_SENDS = 3;
export const PHONE_MAX_WRONG_CODES = 5;

// One per LoginScreen mount (held in a useRef). Counts only sends that went
// out (signInWithPhoneNumber resolved) and wrong codes
// (auth/invalid-verification-code). After 5 wrong codes the code input locks
// for the rest of the mount, and so do further sends (a new code could not be
// entered). sendState: 'ok' | 'wait' (inside the 30 s lock) | 'exhausted'.
export function createPhoneThrottle() {
  let sends = 0;
  let lastSendAt = null;
  let wrongCodes = 0;
  const api = {
    sendLockedUntil() {
      return lastSendAt == null ? 0 : lastSendAt + PHONE_SEND_LOCK_MS;
    },
    sendState(now) {
      if (sends >= PHONE_MAX_SENDS || wrongCodes >= PHONE_MAX_WRONG_CODES) return 'exhausted';
      if (lastSendAt != null && now < lastSendAt + PHONE_SEND_LOCK_MS) return 'wait';
      return 'ok';
    },
    recordSend(now) {
      sends += 1;
      lastSendAt = now;
    },
    recordWrongCode() {
      wrongCodes += 1;
      return { locked: wrongCodes >= PHONE_MAX_WRONG_CODES };
    },
    codeLocked() {
      return wrongCodes >= PHONE_MAX_WRONG_CODES;
    },
    counts() {
      return { sends, wrongCodes };
    },
  };
  return api;
}

// ---------------------------------------------------------------------------
// LoginScreen's invisible reCAPTCHA (S2.4): one verifier at a time, each in a
// fresh host element
// ---------------------------------------------------------------------------

// An invisible RecaptchaVerifier renders straight into the element it is given,
// and its clear() leaves that widget in the DOM (Firebase removes child nodes
// only for a visible widget). grecaptcha refuses a second render into the same
// element, so a verifier rebuilt on the container itself after a send error
// would fail every later send in the mount. Each verifier therefore gets its
// own new child of the container, and drop() empties the container.
//
// getDocument() -> the page document (or null); containerId = the element id
// rendered by the screen; makeVerifier(hostEl) -> a RecaptchaVerifier on that
// element (the screen passes new RecaptchaVerifier(auth, hostEl, { size:
// 'invisible' })). One slot per LoginScreen mount (held in a useRef).
//   get()  -> the current verifier, rendered; builds a fresh one when there is
//             none or when the container left the page (the screen showed its
//             loading view in between).
//   drop() -> clear() the verifier (errors swallowed) and empty the container;
//             call it on a send error and on unmount.
export function createRecaptchaSlot({ getDocument, containerId, makeVerifier }) {
  let verifier = null;
  let host = null;
  const container = () => {
    const d = typeof getDocument === 'function' ? getDocument() : null;
    return d && typeof d.getElementById === 'function' ? d.getElementById(containerId) : null;
  };
  const empty = (el) => {
    if (!el) return;
    if (typeof el.replaceChildren === 'function') {
      el.replaceChildren();
      return;
    }
    while (el.firstChild) el.removeChild(el.firstChild);
  };
  const drop = () => {
    const v = verifier;
    const h = host;
    verifier = null;
    host = null;
    if (v) {
      try { v.clear(); } catch (_) { /* already gone */ }
    }
    if (h && h.parentNode) {
      try { h.parentNode.removeChild(h); } catch (_) { /* already gone */ }
    }
    try { empty(container()); } catch (_) { /* no container */ }
  };
  const get = async () => {
    const box = container();
    if (verifier && (!host || host.parentNode !== box)) drop();
    if (!verifier) {
      if (!box) {
        const err = new Error('reCAPTCHA container missing');
        err.code = 'auth/argument-error';
        throw err;
      }
      empty(box);
      const d = getDocument();
      const h = d.createElement('div');
      box.appendChild(h);
      host = h;
      try {
        verifier = makeVerifier(h);
        await verifier.render();
      } catch (e) {
        drop(); // a widget that never rendered is not reused
        throw e;
      }
    }
    return verifier;
  };
  return { get, drop };
}

// ---------------------------------------------------------------------------
// LoginScreen: the desktop guard, the next allowlist, the blocked line
// ---------------------------------------------------------------------------

export const signedInGuardLine = (userName) => `You're signed in as ${userName}. Sign out first.`;
// A signed-in record with no public profile name yet (a pending account).
export const SIGNED_IN_GUARD_NO_NAME_LINE = "You're signed in. Sign out first.";
export const SIGN_OUT_LABEL = 'Sign out';
export const SIGN_OUT_FAILED_LINE = "Couldn't sign out. Please try again.";

// S2.2 item 8: inside the desktop app a signed-in member never sees the
// sign-in forms (a second sign-in would silently replace them). Returns the
// line that replaces every form, or null (web, or signed out).
// signedIn defaults to "a name was given".
export function loginGuard({ isDesktop, signedIn, currentUserName } = {}) {
  if (!isDesktop) return null;
  const name = typeof currentUserName === 'string' ? currentUserName.trim() : '';
  const isIn = signedIn === undefined ? name !== '' : !!signedIn;
  if (!isIn) return null;
  return name ? signedInGuardLine(name) : SIGNED_IN_GUARD_NO_NAME_LINE;
}

// S7.6 choice 3: /login?next=StreamingAccounts sends a fresh sign-in to the
// Streaming Hub. Anything else is ignored, so a crafted link cannot send a
// sign-in anywhere else.
export const LOGIN_NEXT_ALLOWLIST = Object.freeze(['StreamingAccounts']);
export function nextTarget(params) {
  const next = params ? params.next : undefined;
  return typeof next === 'string' && LOGIN_NEXT_ALLOWLIST.includes(next) ? next : null;
}

// Discord and Twitch leave the page and return to the fixed
// /auth/<provider>/callback URL (code and state only), so the start of the
// sign-in keeps the allowlisted next in sessionStorage and the callback
// takes it back. Every start rewrites or clears the key, so a stale value
// never follows a later sign-in. Storage errors only lose the next.
export const OAUTH_NEXT_KEY = 'oauth_next';

export function getSessionStorage(win) {
  try {
    const w = win || (typeof window !== 'undefined' ? window : undefined);
    return w && w.sessionStorage ? w.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

export function saveOAuthNext(storage, params) {
  const next = nextTarget(params);
  try {
    if (storage) {
      if (next) storage.setItem(OAUTH_NEXT_KEY, next);
      else storage.removeItem(OAUTH_NEXT_KEY);
    }
  } catch (_) {
    // A blocked sessionStorage only means the sign-in lands on Home.
  }
  return next;
}

// Reads and removes the saved next; returns it only when it is still on the
// allowlist.
export function takeOAuthNext(storage) {
  let value = null;
  try {
    if (!storage) return null;
    value = storage.getItem(OAUTH_NEXT_KEY);
    storage.removeItem(OAUTH_NEXT_KEY);
  } catch (_) {
    return null;
  }
  return nextTarget({ next: value });
}

// S2.6: main loads /login?desktopAuth=blocked when a Google sign-in hops to a
// page the app will not host.
export const DESKTOP_AUTH_BLOCKED_LINE = "Google sign-in for this account needs a page CritCast can't open. Sign in with your email, phone, Discord or Twitch.";
export function desktopAuthLine(params, search) {
  let value = params ? params.desktopAuth : undefined;
  if (value === undefined && typeof search === 'string' && search) {
    const m = /[?&]desktopAuth=([^&#]*)/.exec(search);
    value = m ? m[1] : undefined;
  }
  return value === 'blocked' ? DESKTOP_AUTH_BLOCKED_LINE : null;
}

// ---------------------------------------------------------------------------
// Explicit sign-out buttons (S2.2 web item 4)
// ---------------------------------------------------------------------------

// The cap on waiting for the desktop app's answer: a hung bridge never holds
// a sign-out.
export const SIGN_OUT_PUSH_WAIT_MS = 2000;

function defaultDesktopBridge() {
  const w = typeof window !== 'undefined' ? window : undefined;
  return w && w.critcashDesktop ? w.critcashDesktop : null;
}

// In the desktop app: critcashDesktop.auth.pushSession(null, { userAction: true })
// first, then signOut(auth). On the web: signOut(auth) only. The push is never
// sent by the phone deleteUser path (that one is not a member's button).
export async function signOutWithDesktopPush({
  signOut,
  auth,
  isDesktop,
  bridge = defaultDesktopBridge(),
  waitMs = SIGN_OUT_PUSH_WAIT_MS,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
}) {
  if (isDesktop && bridge && bridge.auth && typeof bridge.auth.pushSession === 'function') {
    let timer;
    try {
      await Promise.race([
        Promise.resolve().then(() => bridge.auth.pushSession(null, { userAction: true })),
        new Promise((resolve) => { timer = setTimer(resolve, waitMs); }),
      ]);
    } catch (_) {
      // The push is a courtesy to main; it never blocks the sign-out.
    } finally {
      clearTimer(timer);
    }
  }
  return signOut(auth);
}
